import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCurrentUser,
  updateProfile,
  updateOperativeMails,
} from './userThunks';

/**
 * The signed-in user's own profile.
 *
 * **Not persisted.** Only `auth` is in the persist whitelist, and that is
 * deliberate here: this is asked for on every mount, and a stale copy in
 * localStorage would show the old name for a moment after someone changed it on
 * another device.
 *
 * `isSaving` is kept apart from `isLoading` so a save does not blank the screen
 * the user is editing.
 */
const initialState = {
  data: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

const saved = (state, action) => {
  state.isSaving = false;
  state.data = action.payload;
  state.error = null;
};

const saving = (state) => {
  state.isSaving = true;
  state.error = null;
};

const saveFailed = (state, action) => {
  state.isSaving = false;
  state.error = action.payload;
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(updateProfile.pending, saving)
      .addCase(updateProfile.fulfilled, saved)
      .addCase(updateProfile.rejected, saveFailed)

      .addCase(updateOperativeMails.pending, saving)
      .addCase(updateOperativeMails.fulfilled, saved)
      .addCase(updateOperativeMails.rejected, saveFailed);
  },
});

export const { clearError } = userSlice.actions;

export default userSlice.reducer;
